import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  BadgeCheck,
  Globe2,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Stamp,
  Languages,
  ScrollText,
  Plane,
  Heart,
  FileCheck2,
  CheckCircle2,
  Star,
  ArrowUpRight,
} from "lucide-react";

import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY, BRANCHES } from "@/lib/company";
import { pageHeadForProvince } from "@/lib/seo-helpers";
import {
  buildFaqSchema,
  buildBreadcrumbSchema,
  buildServiceListSchema, buildSpeakableSchema } from "@/lib/schema";
import logo from "@/assets/logo-mark-256.webp";

const TITLE =
  "Notary Public Phuket | English Service for Expats & Investors | Thai Notary Law";
const DESC =
  "Licensed Notary Public, certified translation, MFA legalization, marriage registration and long-term visas (LTR / DTV / Retirement / Privilege) in Phuket — full English service for expats, yacht crews and property investors.";

const SERVICES = [
  {
    icon: Stamp,
    title: "Notary Public",
    desc: "Signature certification, Power of Attorney, certified true copy and affidavits — registered with the Lawyers Council of Thailand under the 2008 Notarial Services Attorney regulation.",
    priceFrom: 1500,
    href: "/services/notary-public-english",
  },
  {
    icon: Languages,
    title: "Certified Translation",
    desc: "NAATI, MoJ-listed and sworn translators across 40+ languages — accepted by embassies, Thai government and overseas immigration.",
    priceFrom: 500,
    href: "/services/translation-certified-all",
  },
  {
    icon: ScrollText,
    title: "MFA & Embassy Legalization",
    desc: "Department of Consular Affairs legalization and embassy authentication — including Apostille support for the new 2026 Convention.",
    priceFrom: 2500,
    href: "/services/mfa-consular-en",
  },
  {
    icon: Heart,
    title: "Marriage Registration",
    desc: "Thai–foreigner marriage registration in Phuket — affidavit, translation, legalization and amphur appointment, end-to-end.",
    priceFrom: 12000,
    href: "/services/marriage-service",
  },
  {
    icon: Plane,
    title: "Long-Term Visas",
    desc: "LTR 10-year, Thailand Privilege 5–20 year, DTV digital nomad, Retirement O-A/O-X — eligibility review and full application.",
    priceFrom: 15000,
    href: "/services/visa-ltr",
  },
  {
    icon: FileCheck2,
    title: "Background & Police Clearance",
    desc: "Thai police clearance, FBI / ACRO / NPC checks for visa, immigration and adoption — apostilled and embassy-legalized.",
    priceFrom: 4500,
    href: "/services/police-clearance-intl",
  },
];

const FAQ = [
  {
    q: "Do you offer service in English in Phuket?",
    a: "Yes. Our team in Phuket speaks English, Russian, Mandarin Chinese and German. Most documents, contracts and consultations are delivered in English with bilingual versions where required by Thai authorities.",
  },
  {
    q: "Where are you located in Phuket?",
    a: "We have offices in Phuket Town, Patong, Cherngtalay (Laguna) and Chalong, and provide on-site visits to villas, hotels and marinas (Royal Phuket Marina, Boat Lagoon, Yacht Haven) across the whole island.",
  },
  {
    q: "Can you handle property purchase (condo / villa) for foreign buyers?",
    a: "Yes — full one-stop service: due diligence on title (Chanote / Nor Sor 3), Sale & Purchase Agreement, Power of Attorney, FET form coordination and registration at the Phuket Land Office. Pricing starts at THB 25,000 plus government fees.",
  },
  {
    q: "How does the Apostille Convention affect Thailand from 2026?",
    a: "Thailand has joined the Apostille Convention effective 2026. Documents issued in member countries no longer need embassy legalization — a single Apostille is accepted. We handle both legacy MFA legalization and the new Apostille routing.",
  },
  {
    q: "I am abroad — can you act for me remotely in Phuket?",
    a: "Yes. We work end-to-end remotely: signed POA at your local notary + Thai embassy legalization, original documents couriered to Phuket, we execute the transaction on your behalf and courier results back worldwide.",
  },
  {
    q: "Do you support yacht crew documents?",
    a: "Yes. Phuket is South-East Asia's yacht hub. We notarize Seaman's Book, Certificate of Competency, Yacht Charter Agreements, Crew Visa documents and bareboat charters at Chalong Bay, Ao Po and the major marinas.",
  },
];

export const Route = createFileRoute("/en/")({
  component: EnglishLanding,
  head: () => ({
    ...pageHeadForProvince({
      title: TITLE,
      description: DESC,
      path: "/en",
      locale: "en_US",
      alternates: [{ lang: "th", path: "/" }],
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "English", url: "/en" },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(FAQ)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildServiceListSchema(
            SERVICES.map((s) => ({
              name: s.title,
              description: s.desc,
              url: s.href,
              priceFrom: s.priceFrom,
            })),
          ),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en")) },
      ],
  }),
});

function EnglishLanding() {
  return (
    <div className="min-h-screen bg-background text-foreground" lang="en">
      {/* Lightweight English header — independent from Thai SiteHeader to keep language clean */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <Link to="/en" className="flex items-center gap-3">
            <img src={logo} alt={COMPANY.brandName} className="h-9 w-9" width="36" height="36" fetchPriority="high" decoding="async" />
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">{COMPANY.brandName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold">
                Phuket · English Desk
              </div>
            </div>
          </Link>
          <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
            <a href="#services" className="hover:text-gold">Services</a>
            <a href="#why" className="hover:text-gold">Why Us</a>
            <a href="#offices" className="hover:text-gold">Offices</a>
            <a href="#faq" className="hover:text-gold">FAQ</a>
            <Link
              to="/"
              className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-accent"
            >
              ภาษาไทย
            </Link>
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90"
            >
              <Phone className="mr-1 inline h-3 w-3" /> {COMPANY.phone}
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-background/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold">
            <ShieldCheck className="h-3 w-3" /> Licensed · Lawyers Council of Thailand
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
            Notary Public &amp; Legal Service in Phuket — built for Expats, Investors and Yacht Crew.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-primary-foreground/90 md:text-xl">
            A registered Thai notarial-services attorney team in Phuket — handling notarization, certified
            translation, MFA/embassy legalization, Thai-foreigner marriage and long-term visas in fluent
            English. Four offices on the island, on-site service to your villa, hotel or marina.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary hover:bg-gold/90"
            >
              <Phone className="h-4 w-4" /> Call {COMPANY.phone}
            </a>
            <a
              href="https://line.me/R/ti/p/@thainotary"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 bg-background/10 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/20"
            >
              <MessageCircle className="h-4 w-4" /> LINE @THAINOTARY
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 bg-background/10 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/20"
            >
              <Mail className="h-4 w-4" /> {COMPANY.email}
            </a>
          </div>
          <div className="mt-10 grid max-w-4xl grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { icon: BadgeCheck, label: "Registered Notarial Attorney" },
              { icon: Globe2, label: "40+ languages translated" },
              { icon: Star, label: "Licensed Notarial Services Attorney" },
              { icon: ShieldCheck, label: "Open 7 days · 08:00–20:00" },
            ].map((b) => (
              <div key={b.label} className="flex items-start gap-2 text-sm">
                <b.icon className="mt-0.5 h-4 w-4 text-gold" />
                <span>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section id="services" className="border-b border-border bg-background py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-gold">Services</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight md:text-4xl">
              Everything an expat or investor needs in one team.
            </h2>
            <p className="mt-4 text-muted-foreground">
              From a single notarized signature to a full villa purchase, marriage or 10-year visa — handled by
              one licensed team in Phuket so you only deal with one point of contact.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.href}
                className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-xl"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-gold/20 group-hover:text-gold">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="font-semibold text-gold">
                    From ฿{s.priceFrom.toLocaleString()}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-gold" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section id="why" className="border-b border-border bg-muted/30 py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gold">Why Thai Notary Law</div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight md:text-4xl">
                A licensed Thai notarial team — not an agency middleman.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Many "notary" services in Phuket are travel agents reselling lawyers. We are the law firm —
                {" "}
                <span className="font-semibold text-foreground">{COMPANY.legalName}</span>, registration
                number {COMPANY.registrationNumber}. Every notarization is signed and stamped by a member of
                the Lawyers Council of Thailand.
              </p>
            </div>
            <ul className="space-y-4">
              {[
                "Direct line to the lawyer handling your file — no call-centre handoffs.",
                "Same-day notarization for standard signature, copy and affidavit work.",
                "Apostille-ready from 2026 — and full legacy MFA + embassy legalization.",
                "Bilingual contracts (English / Thai) drafted side-by-side, not auto-translated.",
                "On-site service at your villa, hotel or marina anywhere on Phuket.",
                "Transparent fixed pricing — quoted in THB before any work starts.",
              ].map((p) => (
                <li key={p} className="flex gap-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section id="offices" className="border-b border-border bg-background py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-xs uppercase tracking-[0.25em] text-gold">Offices on Phuket</div>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight md:text-4xl">
            Four offices · on-site service island-wide.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {BRANCHES.map((b) => (
              <div
                key={b.id}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex items-center gap-2 text-gold">
                  <MapPin className="h-4 w-4" />
                  <div className="text-xs font-bold uppercase tracking-wider">{b.name}</div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.address}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {b.locality} · {b.postalCode}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-border bg-muted/30 py-20">
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-xs uppercase tracking-[0.25em] text-gold">FAQ</div>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight md:text-4xl">
            Frequently asked by expats in Phuket.
          </h2>
          <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-card">
            {FAQ.map((f) => (
              <details key={f.q} className="group p-6">
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-sm font-bold">
                  {f.q}
                  <span className="text-gold transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-16 text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">
            Need it notarized, translated or legalized this week?
          </h2>
          <p className="mt-4 text-primary-foreground/90">
            Send a photo of the document on LINE or email — you'll get a fixed price and timeline within 30
            minutes during opening hours.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary hover:bg-gold/90"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a
              href="https://line.me/R/ti/p/@thainotary"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/10"
            >
              <MessageCircle className="h-4 w-4" /> LINE @THAINOTARY
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
